// ============================================
// MEDFLOW - Patients Page
// ============================================

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, User, Trash2, X, ChevronLeft, ChevronRight, ShieldAlert, ShieldCheck, Eye, Mail, Phone, Calendar, Hash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import { patientsApi } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import {
  addSupabaseClinicalNote,
  createSupabaseClinicalDocument,
  createSupabaseFileAssetSignedUrl,
  createSupabaseFollowUpTask,
  createSupabaseInternalNote,
  createSupabasePatientConsent,
  loadSupabaseClinicalRecord,
  loadSupabasePatientWorkspace,
  saveSupabaseClinicalRecord,
  uploadSupabaseFileAsset,
} from '@/lib/supabase-data';
import { useAuthStore } from '@/stores/auth';
import { formatDate, getInitials } from '@/lib/utils';

interface Patient {
  id: string;
  name: string;
  phone: string;
  email?: string;
  rut?: string;
  birthDate?: string;
  createdAt: string;
  noShowCount?: number;
  isBlocked?: boolean;
  blockedReason?: string | null;
}

interface ClinicalRecordState {
  summary: string;
  currentDiagnosis: string;
  allergies: string;
  chronicConditions: string;
}

interface ClinicalNoteState {
  title: string;
  noteType: string;
  text: string;
}

interface WorkspaceFileItem {
  id: string;
  bucketId: string;
  objectPath: string;
  originalFilename: string;
  mimeType: string | null;
  fileSizeBytes: number;
  category: string;
  uploadedByName: string | null;
  createdAt: string;
}

interface ConsentItem {
  id: string;
  title: string;
  version: string | null;
  status: string;
  acceptedAt: string | null;
  acceptedByName: string | null;
  fileName: string | null;
  createdAt: string;
}

interface ClinicalDocumentItem {
  id: string;
  title: string;
  documentType: string;
  status: string;
  bodyText: string;
  fileName: string | null;
  issuedAt: string | null;
  createdAt: string;
}

interface InternalWorkspaceNote {
  id: string;
  title: string | null;
  body: string;
  visibility: string;
  pinned: boolean;
  authorName: string | null;
  createdAt: string;
}

interface FollowUpTaskItem {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueAt: string | null;
  completedAt: string | null;
  assignedToName: string | null;
  createdByName: string | null;
  createdAt: string;
}

interface AuditLogItem {
  id: string;
  action: string;
  entityType: string;
  source: string;
  actorName: string | null;
  createdAt: string;
}

interface WorkspaceData {
  files: WorkspaceFileItem[];
  consents: ConsentItem[];
  documents: ClinicalDocumentItem[];
  internalNotes: InternalWorkspaceNote[];
  followUpTasks: FollowUpTaskItem[];
  auditLogs: AuditLogItem[];
}

interface ConsentFormState {
  title: string;
  version: string;
  status: 'draft' | 'sent' | 'accepted';
}

interface DocumentFormState {
  title: string;
  documentType: 'prescription' | 'exam_order' | 'medical_order' | 'certificate' | 'clinical_summary' | 'quote' | 'invoice' | 'other';
  bodyText: string;
}

interface InternalNoteFormState {
  title: string;
  body: string;
  visibility: 'clinic' | 'admins' | 'finance' | 'professional_only' | 'support';
}

interface FollowUpFormState {
  title: string;
  description: string;
  dueAt: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

const emptyWorkspace: WorkspaceData = {
  files: [],
  consents: [],
  documents: [],
  internalNotes: [],
  followUpTasks: [],
  auditLogs: [],
};

function formatFileSize(bytes: number) {
  if (!bytes || bytes <= 0) {
    return '0 KB';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
}

export function PatientsPage() {
  const user = useAuthStore((state) => state.user);
  const [search, setSearch] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 15;
  const [showCreate, setShowCreate] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: '', phone: '', email: '' });
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [viewedPatient, setViewedPatient] = useState<Patient | null>(null);
  const [editingPatient, setEditingPatient] = useState<{ name: string; phone: string; email: string; rut: string; birthDate: string } | null>(null);
  const [recordForm, setRecordForm] = useState<ClinicalRecordState>({
    summary: '',
    currentDiagnosis: '',
    allergies: '',
    chronicConditions: '',
  });
  const [noteForm, setNoteForm] = useState<ClinicalNoteState>({
    title: '',
    noteType: 'progress',
    text: '',
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentCategory, setAttachmentCategory] = useState<'image' | 'pdf' | 'document' | 'consent' | 'lab' | 'other'>('document');
  const [consentForm, setConsentForm] = useState<ConsentFormState>({
    title: '',
    version: '',
    status: 'accepted',
  });
  const [documentForm, setDocumentForm] = useState<DocumentFormState>({
    title: '',
    documentType: 'clinical_summary',
    bodyText: '',
  });
  const [internalNoteForm, setInternalNoteForm] = useState<InternalNoteFormState>({
    title: '',
    body: '',
    visibility: 'clinic',
  });
  const [followUpForm, setFollowUpForm] = useState<FollowUpFormState>({
    title: '',
    description: '',
    dueAt: '',
    priority: 'medium',
  });
  const queryClient = useQueryClient();
  const clinic = useAuthStore((state) => state.clinic);

  const loadLegacyPatients = async () => {
    const response = await patientsApi.list({ name: search || undefined });
    return (response?.data as Patient[]) || [];
  };

  const loadSupabasePatients = async () => {
    if (!supabase || !clinic?.id) {
      return [] as Patient[];
    }

    let query = supabase
      .from('patients')
      .select('id, full_name, phone, email, birth_date, created_at')
      .eq('clinic_id', clinic.id)
      .order('created_at', { ascending: false });

    if (search.trim()) {
      query = query.ilike('full_name', `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return ((data ?? []) as Array<Record<string, string | null>>).map((patient) => ({
      id: String(patient.id),
      name: String(patient.full_name ?? ''),
      phone: String(patient.phone ?? 'Sin teléfono'),
      email: patient.email ?? undefined,
      birthDate: patient.birth_date ?? undefined,
      createdAt: String(patient.created_at ?? new Date().toISOString()),
    }));
  };

  const { data, isLoading } = useQuery({
    queryKey: ['patients', clinic?.id, search, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: () => (isSupabaseAuthEnabled ? loadSupabasePatients() : loadLegacyPatients()),
    enabled: Boolean(clinic?.id) || !isSupabaseAuthEnabled,
  });

  const unblockMutation = useMutation({
    mutationFn: (id: string) => patientsApi.unblock(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['patients'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isSupabaseAuthEnabled) {
        if (!supabase) {
          throw new Error('Supabase no está configurado.');
        }

        const { error } = await supabase.from('patients').delete().eq('id', id);

        if (error) {
          throw error;
        }

        return;
      }

      await patientsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!newPatient.name.trim() || !newPatient.phone.trim()) {
        throw new Error('Nombre y teléfono son obligatorios.');
      }

      if (isSupabaseAuthEnabled) {
        if (!supabase || !clinic?.id) {
          throw new Error('Supabase no está configurado.');
        }

        const { error } = await supabase.from('patients').insert({
          clinic_id: clinic.id,
          full_name: newPatient.name.trim(),
          phone: newPatient.phone.trim(),
          email: newPatient.email.trim() || null,
        });

        if (error) {
          throw error;
        }

        return;
      }

      await patientsApi.create({
        name: newPatient.name.trim(),
        phone: newPatient.phone.trim(),
        email: newPatient.email.trim() || undefined,
      });
    },
    onSuccess: () => {
      setNewPatient({ name: '', phone: '', email: '' });
      setShowCreate(false);
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });

  const { data: clinicalRecordData, isLoading: clinicalRecordLoading } = useQuery({
    queryKey: ['patients', 'clinical-record', clinic?.id, selectedPatient?.id],
    queryFn: async () => loadSupabaseClinicalRecord(clinic?.id ?? '', selectedPatient?.id ?? ''),
    enabled: isSupabaseAuthEnabled && Boolean(clinic?.id) && Boolean(selectedPatient?.id),
  });

  const { data: workspaceData, isLoading: workspaceLoading } = useQuery({
    queryKey: ['patients', 'workspace', clinic?.id, selectedPatient?.id],
    queryFn: async () => loadSupabasePatientWorkspace(clinic?.id ?? '', selectedPatient?.id ?? ''),
    enabled: isSupabaseAuthEnabled && Boolean(clinic?.id) && Boolean(selectedPatient?.id),
  });

  const saveRecordMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id) {
        throw new Error('No hay paciente seleccionado.');
      }

      return saveSupabaseClinicalRecord(clinic?.id ?? '', selectedPatient.id, recordForm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', 'clinical-record'] });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id) {
        throw new Error('No hay paciente seleccionado.');
      }

      const recordId =
        clinicalRecordData?.record?.id ??
        (await saveSupabaseClinicalRecord(clinic?.id ?? '', selectedPatient.id, recordForm));

      await addSupabaseClinicalNote(clinic?.id ?? '', selectedPatient.id, recordId, {
        title: noteForm.title,
        noteType: noteForm.noteType,
        text: noteForm.text,
        authorUserId: user?.id ?? null,
      });
    },
    onSuccess: () => {
      setNoteForm({ title: '', noteType: 'progress', text: '' });
      queryClient.invalidateQueries({ queryKey: ['patients', 'clinical-record'] });
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id || !attachmentFile) {
        throw new Error('Debes seleccionar un archivo.');
      }

      return uploadSupabaseFileAsset(clinic?.id ?? '', selectedPatient.id, attachmentFile, {
        recordId: clinicalRecordData?.record?.id ?? null,
        uploadedByUserId: user?.id ?? null,
        category: attachmentCategory,
      });
    },
    onSuccess: () => {
      setAttachmentFile(null);
      setAttachmentCategory('document');
      queryClient.invalidateQueries({ queryKey: ['patients', 'workspace'] });
    },
  });

  const createConsentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id || !consentForm.title.trim()) {
        throw new Error('El título del consentimiento es obligatorio.');
      }

      await createSupabasePatientConsent(clinic?.id ?? '', selectedPatient.id, {
        title: consentForm.title,
        version: consentForm.version,
        status: consentForm.status,
        recordId: clinicalRecordData?.record?.id ?? null,
        acceptedByUserId: user?.id ?? null,
      });
    },
    onSuccess: () => {
      setConsentForm({ title: '', version: '', status: 'accepted' });
      queryClient.invalidateQueries({ queryKey: ['patients', 'workspace'] });
    },
  });

  const createDocumentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id || !documentForm.title.trim()) {
        throw new Error('El documento necesita un título.');
      }

      await createSupabaseClinicalDocument(clinic?.id ?? '', selectedPatient.id, {
        title: documentForm.title,
        documentType: documentForm.documentType,
        bodyText: documentForm.bodyText,
        recordId: clinicalRecordData?.record?.id ?? null,
        createdByUserId: user?.id ?? null,
      });
    },
    onSuccess: () => {
      setDocumentForm({ title: '', documentType: 'clinical_summary', bodyText: '' });
      queryClient.invalidateQueries({ queryKey: ['patients', 'workspace'] });
    },
  });

  const createInternalNoteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id || !internalNoteForm.body.trim()) {
        throw new Error('La nota interna no puede estar vacía.');
      }

      await createSupabaseInternalNote(clinic?.id ?? '', {
        patientId: selectedPatient.id,
        authorUserId: user?.id ?? null,
        title: internalNoteForm.title,
        body: internalNoteForm.body,
        visibility: internalNoteForm.visibility,
      });
    },
    onSuccess: () => {
      setInternalNoteForm({ title: '', body: '', visibility: 'clinic' });
      queryClient.invalidateQueries({ queryKey: ['patients', 'workspace'] });
    },
  });

  const createFollowUpMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient?.id || !followUpForm.title.trim()) {
        throw new Error('La tarea necesita un título.');
      }

      await createSupabaseFollowUpTask(clinic?.id ?? '', {
        patientId: selectedPatient.id,
        createdByUserId: user?.id ?? null,
        assignedToUserId: user?.id ?? null,
        title: followUpForm.title,
        description: followUpForm.description,
        dueAt: followUpForm.dueAt || undefined,
        priority: followUpForm.priority,
      });
    },
    onSuccess: () => {
      setFollowUpForm({ title: '', description: '', dueAt: '', priority: 'medium' });
      queryClient.invalidateQueries({ queryKey: ['patients', 'workspace'] });
    },
  });

  const openClinicalRecord = (patient: Patient) => {
    setSelectedPatient(patient);
    setNoteForm({ title: '', noteType: 'progress', text: '' });
  };

  const exportClinicalRecordPdf = () => {
    if (!selectedPatient) {
      return;
    }

    const noteMarkup = clinicalNotes.length
      ? clinicalNotes
          .map(
            (note) => `
              <section style="margin-top:16px;padding:16px;border:1px solid #e5e7eb;border-radius:12px;">
                <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;">
                  <div>
                    <h3 style="margin:0 0 4px 0;font-size:16px;">${note.title}</h3>
                    <p style="margin:0;color:#6b7280;font-size:12px;">${formatDate(note.createdAt)}</p>
                  </div>
                  <span style="padding:4px 10px;border-radius:999px;background:#f3f4f6;color:#374151;font-size:12px;">${note.noteType}</span>
                </div>
                <p style="margin:12px 0 0 0;white-space:pre-wrap;color:#111827;font-size:14px;line-height:1.6;">${note.text || 'Sin contenido.'}</p>
              </section>
            `
          )
          .join('')
      : '<p style="color:#6b7280;">No hay notas clínicas registradas.</p>';

    const reportWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!reportWindow) {
      return;
    }

    reportWindow.document.write(`
      <html>
        <head>
          <title>Ficha clínica - ${selectedPatient.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 32px; color: #111827; }
            h1, h2, h3, p { margin-top: 0; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .card { border: 1px solid #e5e7eb; border-radius: 14px; padding: 18px; margin-top: 16px; }
            .label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 6px; }
            .value { font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
            @media print { body { margin: 18px; } }
          </style>
        </head>
        <body>
          <header>
            <h1>Ficha clínica</h1>
            <p><strong>Paciente:</strong> ${selectedPatient.name}</p>
            <p><strong>Teléfono:</strong> ${selectedPatient.phone}</p>
            <p><strong>Email:</strong> ${selectedPatient.email || 'Sin email'}</p>
            <p><strong>Fecha de emisión:</strong> ${formatDate(new Date().toISOString())}</p>
          </header>
          <section class="grid">
            <div class="card">
              <div class="label">Resumen</div>
              <div class="value">${recordForm.summary || 'Sin resumen registrado.'}</div>
            </div>
            <div class="card">
              <div class="label">Diagnóstico actual</div>
              <div class="value">${recordForm.currentDiagnosis || 'Sin diagnóstico registrado.'}</div>
            </div>
            <div class="card">
              <div class="label">Alergias</div>
              <div class="value">${recordForm.allergies || 'Sin alergias registradas.'}</div>
            </div>
            <div class="card">
              <div class="label">Condiciones crónicas</div>
              <div class="value">${recordForm.chronicConditions || 'Sin condiciones crónicas registradas.'}</div>
            </div>
          </section>
          <section style="margin-top:24px;">
            <h2>Notas clínicas</h2>
            ${noteMarkup}
          </section>
        </body>
      </html>
    `);
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
  };

  const closeClinicalRecord = () => {
    setSelectedPatient(null);
    setRecordForm({ summary: '', currentDiagnosis: '', allergies: '', chronicConditions: '' });
    setNoteForm({ title: '', noteType: 'progress', text: '' });
    setAttachmentFile(null);
    setAttachmentCategory('document');
    setConsentForm({ title: '', version: '', status: 'accepted' });
    setDocumentForm({ title: '', documentType: 'clinical_summary', bodyText: '' });
    setInternalNoteForm({ title: '', body: '', visibility: 'clinic' });
    setFollowUpForm({ title: '', description: '', dueAt: '', priority: 'medium' });
  };

  const clinicalNotes = clinicalRecordData?.notes ?? [];
  const workspace = workspaceData ?? emptyWorkspace;

  const handleOpenStoredFile = async (file: WorkspaceFileItem) => {
    const signedUrl = await createSupabaseFileAssetSignedUrl(file.objectPath, file.bucketId);
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  const updateMutation = useMutation({
    mutationFn: async ({ id, data: updateData }: { id: string; data: { name?: string; phone?: string; email?: string } }) => {
      if (isSupabaseAuthEnabled) {
        if (!supabase || !clinic?.id) throw new Error('Supabase no está configurado.');
        const mapped: Record<string, string | null> = {};
        if (updateData.name !== undefined) mapped.full_name = updateData.name;
        if (updateData.phone !== undefined) mapped.phone = updateData.phone;
        if (updateData.email !== undefined) mapped.email = updateData.email || null;
        const { error } = await supabase.from('patients').update(mapped).eq('id', id).eq('clinic_id', clinic.id);
        if (error) throw error;
        return;
      }
      await patientsApi.update(id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      setEditingPatient(null);
      if (viewedPatient) {
        const updated = patients.find((p) => p.id === viewedPatient.id);
        if (updated) setViewedPatient(updated);
      }
    },
  });

  const handleDelete = (patient: Patient) => {
    if (confirm(`¿Estás seguro de eliminar a ${patient.name}? Esta acción no se puede deshacer.`)) {
      deleteMutation.mutate(patient.id);
    }
  };

  const calculateAge = (birthDate?: string): string => {
    if (!birthDate) return '—';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age} años`;
  };

  const openPatientDetail = (patient: Patient) => {
    setViewedPatient(patient);
    setEditingPatient(null);
  };

  const startEditingPatient = () => {
    if (!viewedPatient) return;
    setEditingPatient({
      name: viewedPatient.name,
      phone: viewedPatient.phone || '',
      email: viewedPatient.email || '',
      rut: viewedPatient.rut || '',
      birthDate: viewedPatient.birthDate || '',
    });
  };

  const saveEditingPatient = () => {
    if (!viewedPatient || !editingPatient) return;
    updateMutation.mutate({
      id: viewedPatient.id,
      data: {
        name: editingPatient.name.trim(),
        phone: editingPatient.phone.trim(),
        email: editingPatient.email.trim() || undefined,
      },
    });
  };

  const patients = (data as Patient[]) || [];
  const filteredPatients = patients.filter((p) => {
    if (phoneFilter.trim() && !p.phone?.toLowerCase().includes(phoneFilter.trim().toLowerCase())) return false;
    if (emailFilter.trim() && !p.email?.toLowerCase().includes(emailFilter.trim().toLowerCase())) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filteredPatients.length / TABLE_PAGE_SIZE));
  const pagedPatients = filteredPatients.slice((tablePage - 1) * TABLE_PAGE_SIZE, tablePage * TABLE_PAGE_SIZE);
  const selectedRecordId = clinicalRecordData?.record?.id ?? null;

  useEffect(() => {
    if (!selectedPatient) {
      return;
    }

    if (clinicalRecordData?.record) {
      queueMicrotask(() => {
        setRecordForm({
          summary: clinicalRecordData.record.summary,
          currentDiagnosis: clinicalRecordData.record.currentDiagnosis,
          allergies: clinicalRecordData.record.allergies,
          chronicConditions: clinicalRecordData.record.chronicConditions,
        });
      });
      return;
    }

    queueMicrotask(() => {
      setRecordForm({
        summary: '',
        currentDiagnosis: '',
        allergies: '',
        chronicConditions: '',
      });
    });
  }, [clinicalRecordData, selectedPatient]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
        <Button onClick={() => setShowCreate((current) => !current)}>
          <Plus className="w-4 h-4 mr-2" />
          {showCreate ? 'Cerrar formulario' : 'Crear paciente'}
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card>
          <CardContent className="grid gap-4 p-4 md:grid-cols-4">
            <Input
              label="Nombre completo"
              value={newPatient.name}
              onChange={(event) => setNewPatient((current) => ({ ...current, name: event.target.value }))}
              placeholder="Nombre y apellido"
            />
            <Input
              label="Teléfono"
              value={newPatient.phone}
              onChange={(event) => setNewPatient((current) => ({ ...current, phone: event.target.value }))}
              placeholder="+56 9 1234 5678"
            />
            <Input
              label="Email"
              type="email"
              value={newPatient.email}
              onChange={(event) => setNewPatient((current) => ({ ...current, email: event.target.value }))}
              placeholder="paciente@email.com"
            />
            <div className="flex items-end gap-2">
              <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>Guardar</Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="mb-1 block text-xs font-medium text-gray-500">Nombre, RUT o teléfono</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setTablePage(1); }}
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="min-w-[140px]">
              <label className="mb-1 block text-xs font-medium text-gray-500">Teléfono</label>
              <input
                type="text"
                placeholder="Filtrar..."
                value={phoneFilter}
                onChange={(e) => { setPhoneFilter(e.target.value); setTablePage(1); }}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="mb-1 block text-xs font-medium text-gray-500">Correo</label>
              <input
                type="text"
                placeholder="Filtrar..."
                value={emailFilter}
                onChange={(e) => { setEmailFilter(e.target.value); setTablePage(1); }}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {(phoneFilter || emailFilter) && (
              <button
                onClick={() => { setPhoneFilter(''); setEmailFilter(''); setTablePage(1); }}
                className="h-9 px-3 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Patient count */}
      <p className="text-sm text-gray-500">
        {filteredPatients.length === patients.length
          ? <><span className="font-semibold text-gray-700">{patients.length}</span> pacientes registrados</>
          : <><span className="font-semibold text-gray-700">{filteredPatients.length}</span> de {patients.length} pacientes</>}
      </p>

      {/* Table */}
      {isLoading ? (
        <Card><CardContent className="p-6"><div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div></CardContent></Card>
      ) : filteredPatients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-center">
              {search || phoneFilter || emailFilter ? 'No se encontraron pacientes con esos filtros' : 'No hay pacientes registrados'}
            </p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />Agregar paciente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-2.5 w-10 text-center">#</th>
                  <th className="px-3 py-2.5">Nombre</th>
                  <th className="px-3 py-2.5">RUT</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Teléfono</th>
                  <th className="px-3 py-2.5 text-center">Estado</th>
                  <th className="px-3 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedPatients.map((patient, idx) => (
                  <tr key={patient.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5 text-center text-gray-400 text-xs">
                      {(tablePage - 1) * TABLE_PAGE_SIZE + idx + 1}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold shrink-0">
                          {getInitials(patient.name)}
                        </div>
                        <span className="font-medium text-gray-900 truncate max-w-[200px]">{patient.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 font-mono text-xs">{patient.rut || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600 truncate max-w-[180px]">{patient.email || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{patient.phone}</td>
                    <td className="px-3 py-2.5 text-center">
                      {patient.isBlocked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          <ShieldAlert className="w-3 h-3" />Bloqueado
                        </span>
                      ) : (patient.noShowCount ?? 0) > 0 ? (
                        <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
                          {patient.noShowCount} inasist.
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          <ShieldCheck className="w-3 h-3" />Activo
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {patient.isBlocked && (
                          <button
                            onClick={() => unblockMutation.mutate(patient.id)}
                            className="rounded px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                          >
                            Desbloquear
                          </button>
                        )}
                        <button
                          onClick={() => openPatientDetail(patient)}
                          className="rounded px-2.5 py-1 text-xs font-medium text-white bg-primary hover:bg-primary/90 transition-colors inline-flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />Ver
                        </button>
                        <button
                          onClick={() => openClinicalRecord(patient)}
                          disabled={!isSupabaseAuthEnabled}
                          className="rounded px-2.5 py-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-40"
                        >
                          Ficha
                        </button>
                        <button
                          onClick={() => handleDelete(patient)}
                          disabled={deleteMutation.isPending}
                          className="rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Eliminar paciente"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <p className="text-xs text-gray-500">
                Mostrando {(tablePage - 1) * TABLE_PAGE_SIZE + 1}-{Math.min(tablePage * TABLE_PAGE_SIZE, filteredPatients.length)} de {filteredPatients.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTablePage(Math.max(1, tablePage - 1))}
                  disabled={tablePage === 1}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 7) { page = i + 1; }
                  else if (tablePage <= 4) { page = i + 1; }
                  else if (tablePage >= totalPages - 3) { page = totalPages - 6 + i; }
                  else { page = tablePage - 3 + i; }
                  return (
                    <button
                      key={page}
                      onClick={() => setTablePage(page)}
                      className={`min-w-[28px] rounded px-1.5 py-1 text-xs font-medium transition-colors ${
                        page === tablePage ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setTablePage(Math.min(totalPages, tablePage + 1))}
                  disabled={tablePage === totalPages}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Patient Detail Modal */}
      {viewedPatient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-bold shrink-0">
                  {getInitials(viewedPatient.name)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{viewedPatient.name}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    {viewedPatient.isBlocked ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                        <ShieldAlert className="w-3 h-3" />Bloqueado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                        <ShieldCheck className="w-3 h-3" />Activo
                      </span>
                    )}
                    {(viewedPatient.noShowCount ?? 0) > 0 && (
                      <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
                        {viewedPatient.noShowCount} inasist.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editingPatient && (
                  <Button variant="outline" size="sm" onClick={startEditingPatient}>
                    Editar
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => { setViewedPatient(null); setEditingPatient(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {editingPatient ? (
                /* Editing mode */
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Editar datos del paciente</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Nombre completo"
                      value={editingPatient.name}
                      onChange={(e) => setEditingPatient((prev) => prev ? { ...prev, name: e.target.value } : prev)}
                    />
                    <Input
                      label="Teléfono"
                      value={editingPatient.phone}
                      onChange={(e) => setEditingPatient((prev) => prev ? { ...prev, phone: e.target.value } : prev)}
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={editingPatient.email}
                      onChange={(e) => setEditingPatient((prev) => prev ? { ...prev, email: e.target.value } : prev)}
                    />
                    <Input
                      label="RUT"
                      value={editingPatient.rut}
                      onChange={(e) => setEditingPatient((prev) => prev ? { ...prev, rut: e.target.value } : prev)}
                      placeholder="12.345.678-9"
                    />
                    <Input
                      label="Fecha de nacimiento"
                      type="date"
                      value={editingPatient.birthDate}
                      onChange={(e) => setEditingPatient((prev) => prev ? { ...prev, birthDate: e.target.value } : prev)}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Button onClick={saveEditingPatient} loading={updateMutation.isPending}>
                      Guardar cambios
                    </Button>
                    <Button variant="outline" onClick={() => setEditingPatient(null)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                /* View mode - two-column layout */
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Left column */}
                  <div className="space-y-6">
                    {/* Datos de contacto */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Datos de contacto</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                            <Mail className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-medium text-gray-900">{viewedPatient.email || 'Sin email'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                            <Phone className="w-4 h-4 text-green-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Teléfono</p>
                            <p className="text-sm font-medium text-gray-900">{viewedPatient.phone || 'Sin teléfono'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Información personal */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Información personal</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                            <Hash className="w-4 h-4 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">RUT</p>
                            <p className="text-sm font-medium text-gray-900 font-mono">{viewedPatient.rut || '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Nombre completo</p>
                            <p className="text-sm font-medium text-gray-900">{viewedPatient.name}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                            <Calendar className="w-4 h-4 text-purple-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Fecha de nacimiento</p>
                            <p className="text-sm font-medium text-gray-900">
                              {viewedPatient.birthDate ? formatDate(viewedPatient.birthDate) : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                            <Calendar className="w-4 h-4 text-purple-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Edad</p>
                            <p className="text-sm font-medium text-gray-900">{calculateAge(viewedPatient.birthDate)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Información básica</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                          <span className="text-sm text-gray-500">N° de ficha</span>
                          <span className="text-sm font-mono font-medium text-gray-900">{viewedPatient.id.slice(0, 8).toUpperCase()}</span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                          <span className="text-sm text-gray-500">Estado</span>
                          {viewedPatient.isBlocked ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                              <ShieldAlert className="w-3 h-3" />Bloqueado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                              <ShieldCheck className="w-3 h-3" />Activo
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                          <span className="text-sm text-gray-500">Inasistencias</span>
                          <span className={`text-sm font-medium ${(viewedPatient.noShowCount ?? 0) > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>
                            {viewedPatient.noShowCount ?? 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                          <span className="text-sm text-gray-500">Fecha de creación</span>
                          <span className="text-sm font-medium text-gray-900">{formatDate(viewedPatient.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Acciones</h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => { setViewedPatient(null); openClinicalRecord(viewedPatient); }}
                          className="w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                            <Plus className="w-4 h-4 text-emerald-600" />
                          </div>
                          Abrir ficha clínica
                        </button>
                        {viewedPatient.isBlocked && (
                          <button
                            onClick={() => { unblockMutation.mutate(viewedPatient.id); setViewedPatient({ ...viewedPatient, isBlocked: false, noShowCount: 0 }); }}
                            className="w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium text-green-700 hover:bg-green-50 transition-colors"
                          >
                            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                              <ShieldCheck className="w-4 h-4 text-green-600" />
                            </div>
                            Desbloquear paciente
                          </button>
                        )}
                        <button
                          onClick={() => { setViewedPatient(null); handleDelete(viewedPatient); }}
                          className="w-full flex items-center gap-3 rounded-lg border border-red-100 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </div>
                          Eliminar paciente
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedPatient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Ficha clínica · {selectedPatient.name}</h2>
                <p className="text-sm text-gray-500">Resumen clínico y notas evolutivas del paciente.</p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeClinicalRecord}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {!isSupabaseAuthEnabled ? (
              <div className="p-6">
                <p className="text-sm text-gray-600">La ficha clínica detallada está disponible en modo Supabase.</p>
              </div>
            ) : clinicalRecordLoading ? (
              <div className="p-6">
                <div className="h-32 animate-pulse rounded-xl bg-gray-100" />
              </div>
            ) : (
              <div className="space-y-6 px-6 py-5">
                <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="Resumen"
                        value={recordForm.summary}
                        onChange={(event) => setRecordForm((current) => ({ ...current, summary: event.target.value }))}
                        placeholder="Motivo principal, contexto clínico"
                      />
                      <Input
                        label="Diagnóstico actual"
                        value={recordForm.currentDiagnosis}
                        onChange={(event) => setRecordForm((current) => ({ ...current, currentDiagnosis: event.target.value }))}
                        placeholder="Diagnóstico o hipótesis clínica"
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Alergias</span>
                        <textarea
                          rows={4}
                          value={recordForm.allergies}
                          onChange={(event) => setRecordForm((current) => ({ ...current, allergies: event.target.value }))}
                          placeholder="Medicamentos, alimentos, otros"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Condiciones crónicas</span>
                        <textarea
                          rows={4}
                          value={recordForm.chronicConditions}
                          onChange={(event) => setRecordForm((current) => ({ ...current, chronicConditions: event.target.value }))}
                          placeholder="Antecedentes relevantes y patologías crónicas"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        />
                      </label>
                    </div>

                    <div className="flex items-center justify-between rounded-xl border bg-gray-50 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Estado de la ficha</p>
                        <p className="text-xs text-gray-500">
                          {selectedRecordId ? 'La ficha ya existe y se actualizará.' : 'La ficha se creará al guardar por primera vez.'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={exportClinicalRecordPdf}>
                          Exportar PDF
                        </Button>
                        <Button onClick={() => saveRecordMutation.mutate()} loading={saveRecordMutation.isPending}>
                          Guardar ficha
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-xl border p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Nueva nota clínica</h3>
                        <Badge className="bg-blue-100 text-blue-800">Supabase</Badge>
                      </div>

                      <div className="mt-4 space-y-3">
                        <Input
                          label="Título"
                          value={noteForm.title}
                          onChange={(event) => setNoteForm((current) => ({ ...current, title: event.target.value }))}
                          placeholder="Control, evolución, indicaciones"
                        />
                        <label className="space-y-2 block">
                          <span className="text-sm font-medium text-gray-700">Tipo de nota</span>
                          <select
                            value={noteForm.noteType}
                            onChange={(event) => setNoteForm((current) => ({ ...current, noteType: event.target.value }))}
                            className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="progress">Evolución</option>
                            <option value="assessment">Evaluación</option>
                            <option value="plan">Plan</option>
                            <option value="procedure">Procedimiento</option>
                          </select>
                        </label>
                        <label className="space-y-2 block">
                          <span className="text-sm font-medium text-gray-700">Contenido</span>
                          <textarea
                            rows={5}
                            value={noteForm.text}
                            onChange={(event) => setNoteForm((current) => ({ ...current, text: event.target.value }))}
                            placeholder="Describe hallazgos, plan o seguimiento"
                            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                          />
                        </label>

                        <Button onClick={() => addNoteMutation.mutate()} loading={addNoteMutation.isPending}>
                          Agregar nota
                        </Button>
                      </div>
                    </div>

                    <div className="rounded-xl border p-4">
                      <h3 className="font-semibold text-gray-900">Historial clínico</h3>
                      <div className="mt-4 space-y-3 max-h-[320px] overflow-y-auto pr-1">
                        {clinicalNotes.length === 0 ? (
                          <p className="text-sm text-gray-500">Aún no hay notas clínicas registradas.</p>
                        ) : (
                          clinicalNotes.map((note) => (
                            <div key={note.id} className="rounded-lg bg-gray-50 p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-medium text-gray-900">{note.title}</p>
                                  <p className="text-xs text-gray-500">{formatDate(note.createdAt)}</p>
                                </div>
                                <Badge className="bg-gray-100 text-gray-700">{note.noteType}</Badge>
                              </div>
                              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{note.text || 'Sin contenido.'}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Adjuntos clínicos</h3>
                        <p className="text-xs text-gray-500">Sube respaldos, exámenes o consentimientos firmados.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.files.length}</Badge>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-[1fr_160px]">
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Archivo</span>
                        <input
                          type="file"
                          onChange={(event) => setAttachmentFile(event.target.files?.[0] ?? null)}
                          className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-gray-700">Categoría</span>
                        <select
                          value={attachmentCategory}
                          onChange={(event) => setAttachmentCategory(event.target.value as typeof attachmentCategory)}
                          className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="document">Documento</option>
                          <option value="pdf">PDF</option>
                          <option value="image">Imagen</option>
                          <option value="consent">Consentimiento</option>
                          <option value="lab">Laboratorio</option>
                          <option value="other">Otro</option>
                        </select>
                      </label>
                    </div>

                    <div className="mt-3 flex justify-end">
                      <Button onClick={() => uploadFileMutation.mutate()} loading={uploadFileMutation.isPending}>
                        Subir adjunto
                      </Button>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[260px] overflow-y-auto pr-1">
                      {workspaceLoading ? (
                        <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
                      ) : workspace.files.length === 0 ? (
                        <p className="text-sm text-gray-500">Aún no hay archivos asociados a este paciente.</p>
                      ) : (
                        workspace.files.map((file) => (
                          <div key={file.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{file.originalFilename}</p>
                                <p className="text-xs text-gray-500">
                                  {file.category} · {formatFileSize(file.fileSizeBytes)} · {formatDate(file.createdAt)}
                                </p>
                                {file.uploadedByName && <p className="text-xs text-gray-500">Subido por {file.uploadedByName}</p>}
                              </div>
                              <Button variant="outline" size="sm" onClick={() => void handleOpenStoredFile(file)}>
                                Abrir
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Documentos clínicos</h3>
                        <p className="text-xs text-gray-500">Órdenes, recetas, certificados y resúmenes.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.documents.length}</Badge>
                    </div>

                    <div className="mt-4 space-y-3">
                      <Input
                        label="Título"
                        value={documentForm.title}
                        onChange={(event) => setDocumentForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Resumen post procedimiento"
                      />
                      <label className="space-y-2 block">
                        <span className="text-sm font-medium text-gray-700">Tipo</span>
                        <select
                          value={documentForm.documentType}
                          onChange={(event) => setDocumentForm((current) => ({ ...current, documentType: event.target.value as DocumentFormState['documentType'] }))}
                          className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="clinical_summary">Resumen clínico</option>
                          <option value="prescription">Receta</option>
                          <option value="exam_order">Orden de examen</option>
                          <option value="medical_order">Orden médica</option>
                          <option value="certificate">Certificado</option>
                          <option value="quote">Presupuesto</option>
                          <option value="invoice">Factura</option>
                          <option value="other">Otro</option>
                        </select>
                      </label>
                      <label className="space-y-2 block">
                        <span className="text-sm font-medium text-gray-700">Contenido</span>
                        <textarea
                          rows={4}
                          value={documentForm.bodyText}
                          onChange={(event) => setDocumentForm((current) => ({ ...current, bodyText: event.target.value }))}
                          placeholder="Texto libre o resumen estructurado del documento"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        />
                      </label>
                      <div className="flex justify-end">
                        <Button onClick={() => createDocumentMutation.mutate()} loading={createDocumentMutation.isPending}>
                          Guardar documento
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[260px] overflow-y-auto pr-1">
                      {workspace.documents.length === 0 ? (
                        <p className="text-sm text-gray-500">Sin documentos clínicos todavía.</p>
                      ) : (
                        workspace.documents.map((document) => (
                          <div key={document.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{document.title}</p>
                                <p className="text-xs text-gray-500">
                                  {document.documentType} · {document.status} · {formatDate(document.issuedAt ?? document.createdAt)}
                                </p>
                                {document.fileName && <p className="text-xs text-gray-500">Adjunto: {document.fileName}</p>}
                              </div>
                              <Badge className="bg-gray-100 text-gray-700">{document.status}</Badge>
                            </div>
                            {document.bodyText && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{document.bodyText}</p>}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Consentimientos</h3>
                        <p className="text-xs text-gray-500">Registra aceptación, envío o borradores.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.consents.length}</Badge>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <Input
                        label="Título"
                        value={consentForm.title}
                        onChange={(event) => setConsentForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Consentimiento tratamiento láser"
                      />
                      <Input
                        label="Versión"
                        value={consentForm.version}
                        onChange={(event) => setConsentForm((current) => ({ ...current, version: event.target.value }))}
                        placeholder="v1.0"
                      />
                    </div>

                    <label className="mt-3 space-y-2 block">
                      <span className="text-sm font-medium text-gray-700">Estado</span>
                      <select
                        value={consentForm.status}
                        onChange={(event) => setConsentForm((current) => ({ ...current, status: event.target.value as ConsentFormState['status'] }))}
                        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="draft">Borrador</option>
                        <option value="sent">Enviado</option>
                        <option value="accepted">Aceptado</option>
                      </select>
                    </label>

                    <div className="mt-3 flex justify-end">
                      <Button onClick={() => createConsentMutation.mutate()} loading={createConsentMutation.isPending}>
                        Registrar consentimiento
                      </Button>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[260px] overflow-y-auto pr-1">
                      {workspace.consents.length === 0 ? (
                        <p className="text-sm text-gray-500">No hay consentimientos cargados.</p>
                      ) : (
                        workspace.consents.map((consent) => (
                          <div key={consent.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{consent.title}</p>
                                <p className="text-xs text-gray-500">
                                  {consent.version || 'Sin versión'} · {formatDate(consent.createdAt)}
                                </p>
                                {consent.acceptedAt && <p className="text-xs text-gray-500">Aceptado: {formatDate(consent.acceptedAt)}</p>}
                                {consent.acceptedByName && <p className="text-xs text-gray-500">Registrado por {consent.acceptedByName}</p>}
                                {consent.fileName && <p className="text-xs text-gray-500">Archivo: {consent.fileName}</p>}
                              </div>
                              <Badge className="bg-gray-100 text-gray-700">{consent.status}</Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Seguimiento clínico</h3>
                        <p className="text-xs text-gray-500">Próximos pasos, llamados o tareas post atención.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.followUpTasks.length}</Badge>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <Input
                        label="Título"
                        value={followUpForm.title}
                        onChange={(event) => setFollowUpForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Llamar para control de 7 días"
                      />
                      <Input
                        label="Vencimiento"
                        type="datetime-local"
                        value={followUpForm.dueAt}
                        onChange={(event) => setFollowUpForm((current) => ({ ...current, dueAt: event.target.value }))}
                      />
                    </div>

                    <label className="mt-3 space-y-2 block">
                      <span className="text-sm font-medium text-gray-700">Prioridad</span>
                      <select
                        value={followUpForm.priority}
                        onChange={(event) => setFollowUpForm((current) => ({ ...current, priority: event.target.value as FollowUpFormState['priority'] }))}
                        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="low">Baja</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="urgent">Urgente</option>
                      </select>
                    </label>

                    <label className="mt-3 space-y-2 block">
                      <span className="text-sm font-medium text-gray-700">Descripción</span>
                      <textarea
                        rows={4}
                        value={followUpForm.description}
                        onChange={(event) => setFollowUpForm((current) => ({ ...current, description: event.target.value }))}
                        placeholder="Indicaciones o contexto para el seguimiento"
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      />
                    </label>

                    <div className="mt-3 flex justify-end">
                      <Button onClick={() => createFollowUpMutation.mutate()} loading={createFollowUpMutation.isPending}>
                        Crear tarea
                      </Button>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[260px] overflow-y-auto pr-1">
                      {workspace.followUpTasks.length === 0 ? (
                        <p className="text-sm text-gray-500">No hay seguimientos pendientes.</p>
                      ) : (
                        workspace.followUpTasks.map((task) => (
                          <div key={task.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{task.title}</p>
                                <p className="text-xs text-gray-500">
                                  {task.priority} · {task.status} · {task.dueAt ? formatDate(task.dueAt) : 'Sin vencimiento'}
                                </p>
                                {task.assignedToName && <p className="text-xs text-gray-500">Asignado a {task.assignedToName}</p>}
                                {task.description && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{task.description}</p>}
                              </div>
                              <Badge className="bg-gray-100 text-gray-700">{task.status}</Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Notas internas</h3>
                        <p className="text-xs text-gray-500">Contexto operativo visible solo al equipo.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.internalNotes.length}</Badge>
                    </div>

                    <div className="mt-4 space-y-3">
                      <Input
                        label="Título"
                        value={internalNoteForm.title}
                        onChange={(event) => setInternalNoteForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Observación de recepción"
                      />
                      <label className="space-y-2 block">
                        <span className="text-sm font-medium text-gray-700">Visibilidad</span>
                        <select
                          value={internalNoteForm.visibility}
                          onChange={(event) => setInternalNoteForm((current) => ({ ...current, visibility: event.target.value as InternalNoteFormState['visibility'] }))}
                          className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="clinic">Clínica</option>
                          <option value="admins">Admins</option>
                          <option value="finance">Finanzas</option>
                          <option value="professional_only">Solo profesional</option>
                          <option value="support">Support</option>
                        </select>
                      </label>
                      <label className="space-y-2 block">
                        <span className="text-sm font-medium text-gray-700">Contenido</span>
                        <textarea
                          rows={4}
                          value={internalNoteForm.body}
                          onChange={(event) => setInternalNoteForm((current) => ({ ...current, body: event.target.value }))}
                          placeholder="Información interna relevante para la atención o coordinación"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        />
                      </label>
                      <div className="flex justify-end">
                        <Button onClick={() => createInternalNoteMutation.mutate()} loading={createInternalNoteMutation.isPending}>
                          Guardar nota interna
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[260px] overflow-y-auto pr-1">
                      {workspace.internalNotes.length === 0 ? (
                        <p className="text-sm text-gray-500">Sin notas internas registradas.</p>
                      ) : (
                        workspace.internalNotes.map((note) => (
                          <div key={note.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{note.title || 'Nota interna'}</p>
                                <p className="text-xs text-gray-500">
                                  {note.visibility} · {formatDate(note.createdAt)}{note.authorName ? ` · ${note.authorName}` : ''}
                                </p>
                              </div>
                              {note.pinned && <Badge className="bg-amber-100 text-amber-800">Fijada</Badge>}
                            </div>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{note.body}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Auditoría del paciente</h3>
                        <p className="text-xs text-gray-500">Eventos relevantes asociados a la ficha.</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{workspace.auditLogs.length}</Badge>
                    </div>

                    <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto pr-1">
                      {workspace.auditLogs.length === 0 ? (
                        <p className="text-sm text-gray-500">Todavía no hay eventos de auditoría visibles.</p>
                      ) : (
                        workspace.auditLogs.map((event) => (
                          <div key={event.id} className="rounded-lg bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-900">{event.action} · {event.entityType}</p>
                                <p className="text-xs text-gray-500">
                                  {event.actorName || 'Sistema'} · {event.source} · {formatDate(event.createdAt)}
                                </p>
                              </div>
                              <Badge className="bg-gray-100 text-gray-700">{event.source}</Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
